import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./LoginForm.css";
import { setUserCookie } from "../../context/cookieUtils";
import Button from "../../OwlyManage/components/Buttons/Button/Button";
import { useTranslation } from "react-i18next";


export default function LoginForm({
  setUser,
  onAuthenticated,
  onRegisterLink,
  useProd = true
}) {
  const { t } = useTranslation('auth');
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const loginUrl = process.env.NODE_ENV === "production"
          ? process.env.REACT_APP_BASE_URL_PROD + "/api/auth/login/"
          : process.env.REACT_APP_BASE_URL_DEV + "/api/auth/login/";

      const response = await fetch(loginUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      const data = await response.json();

      if (!response.ok) {
        let errorMsg = t('login.errors.login_failed');
        if (response.status === 401 || response.status === 403) {
          errorMsg = t('login.errors.invalid_credentials');
        } else if (response.status >= 500) {
          errorMsg = t('login.errors.server_error');
        } else if (response.status === 0) {
          errorMsg = t('login.errors.network_error');
        } else if (typeof data === "object" && data !== null && !Array.isArray(data)) {
          const fieldLabels = {
            username: t('register.fieldLabels.username'),
            password: t('register.fieldLabels.password'),
          };
          const messages = Object.entries(data).flatMap(([field, msgs]) => {
            // Si es non_field_errors con mensaje español de credenciales, reemplaza por inglés
            if (field === "non_field_errors" && Array.isArray(msgs)) {
              return msgs.map(msg =>
                msg.includes("No es posible iniciar sesión")
                  ? t('login.errors.invalid_credentials')
                  : msg
              );
            }
            const label = fieldLabels[field] || field;
            return Array.isArray(msgs)
              ? msgs.map((msg) => `${label}: ${msg}`)
              : [`${label}: ${msgs}`];
          });
          errorMsg = messages.join("\n");
        } else if (data && (data.detail || data.message)) {
          errorMsg = data.detail || data.message;
        }
        setError(errorMsg);
        return;
      }
      if (data.access && data.user && data.refresh) {
        const organization = data.organization || data.user.organization;
        const organization_id = data.organization_id || (organization && organization.id) || (data.user.organization_id);
        const userObj = {
          username: data.user.username,
          email: data.user.email,
          access: data.access,
          refresh: data.refresh,
          is_staff: data.user.is_staff,
          role_id: data.user.role_id,
          organization_id,
          organization,
        };
        setUserCookie(userObj);
        if (setUser) setUser(userObj);

        if (onAuthenticated) onAuthenticated(userObj);
        navigate("/app", { replace: true });
      } else {
        setError(t('login.errors.no_token'));
      }
    } catch (err) {
      setError(t('login.errors.server_error'));
    }
  };

  return (
    <div>
      <h2>{t('login.title')}</h2>
      <p className="register-text">
        {t('login.cta_register_prefix')} {" "}
        <button
          type="button"
          className="as-link"
          onClick={(e) => {
            e.preventDefault();
            if (onRegisterLink) onRegisterLink();
          }}
        >
          {t('login.cta_register_link')}
        </button>{" "}
        {t('login.cta_register_suffix')}
      </p>
      <form className="login-form" onSubmit={handleSubmit}>
        <input
          type="text"
          name="username"
          placeholder={t('login.username_placeholder')}
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
          className="login-input"
        />
        <div className="password-input-wrapper">
          <input
            type={showPassword ? "text" : "password"}
            name="password"
            placeholder={t('login.password_placeholder')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="login-input-password"
          />
          <button
            type="button"
            className="password-toggle-btn-auth"
            aria-label={showPassword ? t('reset.fields.password_label') : t('reset.fields.password_label')}
            onClick={() => setShowPassword((v) => !v)}
            tabIndex={0}
          >
            {showPassword ? (
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#888"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M1 1l22 22" />
                <path d="M17.94 17.94A10.05 10.05 0 0 1 12 19C7 19 2.73 15.11 1 12c.74-1.32 1.81-2.87 3.22-4.29M9.53 9.53A3.5 3.5 0 0 1 12 8.5a3.5 3.5 0 0 1 3.5 3.5c0 .47-.09.91-.26 1.32" />
              </svg>
            ) : (
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#888"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <ellipse cx="12" cy="12" rx="10" ry="7" />
                <circle cx="12" cy="12" r="3.5" />
              </svg>
            )}
          </button>
        </div>
        {error && (
          <div className="login-error" style={{
            maxWidth: "100%",
            whiteSpace: "pre-line",
            overflow: "hidden",
            textOverflow: "ellipsis",
            minHeight: 32,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}>{error}</div>
        )}
        <Button type="submit" variant="primary" size="md">
          {t('login.submit')}
        </Button>
      </form>
      
    </div>
  );
}
